import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { MdOutlineContentCopy, MdInfoOutline, MdVisibility, MdVisibilityOff } from "react-icons/md";
import { FaAngleLeft } from "react-icons/fa";
import { Modal } from 'bootstrap';
import { getVirtualCardDetails, getVirtualCardSensitiveData } from "../../../../api/api";
import Loader from "../../../../components/Loader/Loader";
import TransactionPinModal from "../../../../components/TransactionPinModal";

const CardDetails = () => {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const initialPin = location.state?.pin;
  
  const [card, setCard] = useState(null);
  const [sensitiveData, setSensitiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealing, setRevealing] = useState(false);
  const [copiedText, setCopiedText] = useState(null);
  const hasPromptedForPin = useRef(false);

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const response = await getVirtualCardDetails(cardId);
        if (response.data?.data) {
            const cardData = response.data.data;
            setCard(cardData);
        }
      } catch (error) {
        toast.error("Failed to load card details");
      } finally {
        setLoading(false);
      }
    };
    fetchCard();
  }, [cardId]);

  const handlePinSubmit = async (pinValue) => {
    setRevealing(true);
    try {
      const response = await getVirtualCardSensitiveData(cardId, { pin: pinValue });
      setSensitiveData(response.data?.data);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch card sensitive details");
    } finally {
      setRevealing(false);
    }
  };

  useEffect(() => {
    if (initialPin) {
      handlePinSubmit(initialPin);
    }
  }, [cardId, initialPin]);

  useEffect(() => {
    if (loading || !card || initialPin || hasPromptedForPin.current) return;

    const modalEl = document.getElementById("enterPinModal");
    if (!modalEl) return;

    hasPromptedForPin.current = true;
    const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
    modal.show();
  }, [card, initialPin, loading]);

  const handleCopy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedText(text); 
    setTimeout(() => setCopiedText(null), 5000);
  };

  if (loading) return <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '60vh' }}><Loader text="Loading card details..." /></div>;
  if (!card) return <div className="text-center py-5">Card not found.</div>;

  const renderRow = (label, value, copyableText = null, isSensitive = false) => (
    <div className="d-flex justify-content-between align-items-center py-3" style={{ borderBottom: '1px solid #f1f3f5' }}>
      <span className="text-muted fw-semibold" style={{ fontSize: '13px', color: '#6C7A9C' }}>{label}</span>
      <div className="d-flex align-items-center gap-3">
        <span className="text-dark fw-semibold font-monospace" style={{ fontSize: '14px', color: '#19064B' }}>{value}</span>
        
        {(copyableText || isSensitive) && (
          <div className="d-flex align-items-center gap-2">
            {copyableText && (
              <button 
                onClick={() => handleCopy(copyableText)} 
                className="btn btn-sm btn-link p-0 text-primary border-0 position-relative"
                title="Copy"
                style={{ transition: 'transform 0.2s' }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <MdOutlineContentCopy size={16} />
                {copiedText === copyableText && (
                   <span className="position-absolute bg-dark text-white rounded shadow" style={{ top: '-28px', right: '-10px', padding: '3px 6px', fontSize: '10px', zIndex: 10 }}>Copied!</span>
                )}
              </button>
            )}
            {isSensitive && !sensitiveData && (
              <button 
                onClick={() => {
                  const modalEl = document.getElementById('enterPinModal');
                  const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
                  modal.show();
                }} 
                className="btn btn-sm btn-link p-0 text-secondary border-0 text-decoration-none fw-semibold"
                title="Reveal"
                disabled={revealing}
                style={{ transition: 'transform 0.2s', fontSize: '13px', color: '#0A95FF' }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                 {revealing ? <span className="spinner-border spinner-border-sm" style={{ width: '14px', height: '14px' }} role="status" aria-hidden="true"></span> : "Reveal"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const walletBalance = sensitiveData?.card_wallet?.balance
    ?? card.virtual_card_account?.balance
    ?? card.balance;
  const walletCurrency = sensitiveData?.card_wallet?.currency
    || card.virtual_card_account?.currency
    || card.currency
    || "USD";
  const balanceDisplay = walletBalance !== undefined && walletBalance !== null
    ? `${Number(walletBalance).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ${walletCurrency}`
    : "—";
  const cardType = card.virtual_card_brand?.network || card.card_brand || " ";
  const nameOnCard = sensitiveData?.name || card.card_holder?.full_name || card.cardholderName || card.card_name || "";
  
  const cardNumberDisplay = sensitiveData?.card_number 
    ? sensitiveData.card_number.match(/.{1,4}/g)?.join(' ') 
    : (sensitiveData?.pan ? sensitiveData.pan.match(/.{1,4}/g)?.join(' ') : (card.last4 ? `**** **** **** ${card.last4}` : "**** **** **** ****"));
  const cardNumberCopy = sensitiveData?.card_number || sensitiveData?.pan;

  const expiryDisplay = sensitiveData 
    ? `${sensitiveData.expiry_month}/${sensitiveData.expiry_year}` 
    : "**/**";
  const expiryCopy = sensitiveData ? `${sensitiveData.expiry_month}/${sensitiveData.expiry_year}` : null;
  
  const cvvDisplay = sensitiveData?.cvv ? sensitiveData.cvv : "***";
  const cvvCopy = sensitiveData?.cvv;

  const status = card.status;
  const country = sensitiveData?.billing_address?.iso2_country_code || card.iso2_country_code || '-';
  const stateRegion = sensitiveData?.billing_address?.state || card.state || '';
  const street = sensitiveData?.billing_address?.address || card.address || '';
  const city = sensitiveData?.billing_address?.city || card.city || '';
  const postalCode = sensitiveData?.billing_address?.postal_code || card.postal_code || '';

  const renderStatus = (statusVal) => {
    const s = statusVal.toLowerCase();
    let bgClass = 'bg-success text-success';
    if (s === 'declined' || s === 'blocked' || s === 'frozen' || s === 'failed') {
      bgClass = 'bg-danger text-danger';
    } else if (s === 'pending') {
      bgClass = 'bg-warning text-warning';
    }
    return (
      <span className={`badge ${bgClass} bg-opacity-10 border border-opacity-25 px-3 py-1.5 rounded-pill fw-semibold text-uppercase`} style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
        {statusVal}
      </span>
    );
  };

  return (
    <section className="create__card__page card__options__page" style={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      <section className="create-card_section options__card__section pt-4">
        <div className="container py-4" style={{ maxWidth: '750px', margin: '0 auto' }}>
          
          {/* Back Button */}
          <div className="d-flex align-items-center mb-4">
              <button 
                  onClick={() => navigate(-1)} 
                  className="btn btn-light d-flex align-items-center shadow-sm" 
                  style={{ 
                      borderRadius: '20px', 
                      padding: '6px 16px',
                      backgroundColor: '#ffffff',
                      border: '1px solid #E0E4EC',
                      color: '#19064B',
                      fontWeight: '500',
                      fontSize: '13px',
                      gap: '6px'
                  }}
              >
                  <FaAngleLeft size={13} />
                  <span>Go back</span>
              </button>
          </div>

          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
             <h5 className="mb-0 fw-bold text-dark" style={{ color: '#19064B' }}>Card Details</h5>
             <button onClick={() => navigate('/user/cards/activity-logs')} className="btn btn-link text-primary text-decoration-none p-0 fw-semibold" style={{ fontSize: '13px', color: '#0A95FF' }}>
                View Transactions
             </button>
          </div>

          <div className="cardDetails__list mb-5">
            {renderRow("Name on Card", nameOnCard, nameOnCard)}
            {renderRow("Card Number", cardNumberDisplay, cardNumberCopy, true)}
            {renderRow("Expiry date", expiryDisplay, expiryCopy, true)}
            {renderRow("CVV", cvvDisplay, cvvCopy, true)}
            {renderRow("Balance", balanceDisplay)}
          </div>

          <div className="billing__address__list mt-5">
             <h5 className="mb-3 fw-bold text-dark pb-2 border-bottom" style={{ color: '#19064B' }}>Billing Address</h5>
             {renderRow("Street", street)}
             {renderRow("City", city)}
             {renderRow("State/Province", stateRegion)}
             {renderRow("Postal Code", postalCode)}
             {renderRow("Country", country)}
          </div>
        </div>
      </section>

      {/* Enter PIN Modal */}
      <TransactionPinModal
        id="enterPinModal"
        onSubmit={handlePinSubmit}
        title="Enter Transaction PIN"
        description="Enter your 4-digit PIN to securely view your card details."
      />
    </section>
  );
};

export default CardDetails;
